<template>
  <div
    class="v2-related-card"
    :class="{ 'v2-related-card--no-hover': isWishlistDropdownOpened }"
  >
    <span
      v-if="product.keyStyles"
      class="v2-related-card__key-style"
    >
      {{ product.keyStyles }}
    </span>
    <span
      v-if="PRODUCT_STATUS.CANCELLED.VALUE === product.status"
      class="v2-related-card__status v2-related-card__status--cancelled"
    >
      {{ PRODUCT_STATUS.CANCELLED.LABEL }}
    </span>

    <div
      class="v2-related-card__image-wrap"
      @click="openProductDetail(product)"
    >
      <div class="v2-related-card__image-inner">
        <img
          :src="parseProductImage(product)"
          :alt="`${product.name}-${product._id}`"
          class="v2-related-card__image"
          @error="onImageError"
        >
      </div>
      <div
        v-if="visibleColors.length"
        class="v2-related-card__colors"
      >
        <span
          v-if="visibleColors.length > maxVisibleColors"
          class="v2-related-card__color-more"
        >
          +{{ visibleColors.length - maxVisibleColors }}
        </span>
        <span
          v-for="(pc, index) in visibleColors.slice(0, maxVisibleColors)"
          :key="index"
          class="v2-related-card__color-dot"
          :class="{ 'v2-related-card__color-dot--bordered': classForWhiteColor(pc.colorFamily, pc.hexCode) }"
          :style="'background:' + getColorCode(pc.colorFamily, pc.hexCode)"
          :data-tooltip="pc.color"
        />
      </div>
    </div>

    <div class="v2-related-card__body">
      <div
        class="v2-related-card__name"
        :title="product.name"
        @click="openProductDetail(product)"
      >
        {{ product.name }}
      </div>
    </div>

    <div class="v2-related-card__actions">
      <button
        v-if="isRetailer"
        class="v2-related-card__cart-btn"
        :class="{ 'v2-related-card__cart-btn--in-cart': product.isInCart }"
        :disabled="inactiveCollection"
        @click="handleAddToCart"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="9"
            cy="21"
            r="1"
          />
          <circle
            cx="20"
            cy="21"
            r="1"
          />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
        <span>{{ product.isInCart ? 'View In Cart' : 'Add to Cart' }}</span>
      </button>
      <v2-product-wishlists-dropdown
        :product="product"
        :wishlists="wishlists"
        :disabled="inactiveCollection"
        :is-selections="isBrand"
        :product-wishlists-count="product.wishlists ? product.wishlists.length : 0"
        @select-wishlist-on-product="$emit('select-wishlist-on-product', $event)"
        @toggle-wishlist-dropdown="$emit('toggle-wishlist-dropdown', $event)"
        @create-wishlist="$emit('create-wishlist')"
      />
    </div>
  </div>
</template>

<script>
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { getDefaultProductImage } from '@/@core/utils/utils'
import { getColorCode, classForWhiteColor } from '@/product-colors'
import V2ProductWishlistsDropdown from '@/components/v2/V2ProductWishlistsDropdown.vue'
import { useEcommerceUi } from '@/views/apps/useEcommerce'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'

const { PRODUCT_STATUS } = c

export default {
  name: 'V2RelatedProductCard',
  components: {
    V2ProductWishlistsDropdown,
  },
  mixins: [UserRoleMixin],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    inactiveCollection: {
      type: Boolean,
      default: false,
    },
    wishlistId: {
      type: String,
      default: '',
    },
    wishlists: {
      type: Array,
      default: () => [],
    },
    isWishlistDropdownOpened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      PRODUCT_STATUS,
      getColorCode,
      classForWhiteColor,
      maxVisibleColors: 5,
    }
  },
  computed: {
    // Skip colours the map cannot resolve, same as ShopProducts. Filtering
    // before the slice also keeps the "+N" count matching the dots shown.
    visibleColors() {
      return (this.product.colors || []).filter(
        pc => getColorCode(pc.colorFamily, pc.hexCode) !== null,
      )
    },
  },
  methods: {
    parseProductImage(product) {
      if (product?.thumbnail) return product.thumbnail
      if (product?.images?.length && product.images[0]) return product.images[0]
      return getDefaultProductImage()
    },
    onImageError(event) {
      event.target.src = getDefaultProductImage()
    },
    openProductDetail(product) {
      // KP-3382: don't navigate from here. Let the parent decide how to open —
      // in the modal flow it swaps the popup content in place; on the standalone
      // product page it navigates in-app. Opening a new tab / full reload breaks
      // the PLG-iframe auth, and a route push would close the popup.
      this.$emit('open-product', product)
    },
    handleAddToCart() {
      this.handleCartActionClick({
        product: this.product,
        location: 'relatedProductCard',
        conflictResolveCallback: 'updateProductAfterConflict',
      })
    },
    updateProductAfterConflict(updatedProduct) {
      updatedProduct.isInCart = true
    },
  },
  setup() {
    const { handleCartActionClick } = useEcommerceUi()
    return { handleCartActionClick }
  },
}
</script>

<style lang="scss" scoped>
.v2-related-card {
  position: relative;
  background: var(--v2-card);
  border: 1px solid var(--v2-border);
  border-radius: var(--v2-radius-lg);
  overflow: visible;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover:not(&--no-hover) {
    box-shadow: var(--v2-shadow-md);
    transform: translateY(-2px);
  }

  &--no-hover {
    pointer-events: auto;
  }

  &__key-style {
    position: absolute;
    top: var(--v2-space-2);
    left: var(--v2-space-2);
    z-index: 1;
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 2px var(--v2-space-2);
    border-radius: var(--v2-radius-sm);
    text-transform: capitalize;
  }

  &__status {
    position: absolute;
    top: var(--v2-space-2);
    right: var(--v2-space-2);
    z-index: 1;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 2px var(--v2-space-2);
    border-radius: var(--v2-radius-sm);

    &--cancelled {
      background: var(--v2-destructive);
      color: #fff;
    }
  }

  &__image-wrap {
    position: relative;
    cursor: pointer;
  }

  &__image-inner {
    aspect-ratio: 2/3;
    overflow: hidden;
    border-radius: var(--v2-radius-lg) var(--v2-radius-lg) 0 0;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }

  &__colors {
    position: absolute;
    bottom: var(--v2-space-2);
    right: var(--v2-space-2);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    z-index: 3;
  }

  &__color-more {
    font-size: 0.625rem;
    font-weight: 600;
    color: var(--v2-foreground);
    background: rgba(255, 255, 255, 0.85);
    border-radius: 999px;
    padding: 1px 5px;
    line-height: 1.2;
  }

  &__color-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--v2-border);
    position: relative;

    &--bordered {
      border-color: var(--v2-border);
    }

    &[data-tooltip]:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      top: 50%;
      right: calc(100% + 8px);
      transform: translateY(-50%);
      background: var(--v2-foreground);
      color: #fff;
      font-size: 0.625rem;
      font-weight: 500;
      padding: 2px 6px;
      border-radius: 4px;
      white-space: nowrap;
      pointer-events: none;
      z-index: 100;
    }

    &[data-tooltip]:hover::before {
      content: '';
      position: absolute;
      top: 50%;
      right: calc(100% + 0px);
      transform: translateY(-50%);
      border: 4px solid transparent;
      border-left-color: var(--v2-foreground);
      z-index: 100;
    }
  }

  &__body {
    padding: var(--v2-space-2) var(--v2-space-3);
  }

  &__name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__actions {
    display: flex;
    align-items: stretch;
    border-top: 1px solid var(--v2-border);
    min-height: 42px;

    ::v-deep .v2-wishlists-dropdown {
      flex-shrink: 0;
    }

    ::v-deep .v2-wishlists-dropdown__toggle {
      border-top: none;
      border-left: 1px solid var(--v2-border);
      border-bottom-right-radius: var(--v2-radius-lg);
      min-height: 42px;
    }

    ::v-deep .v2-wishlists-dropdown__menu {
      bottom: 100%;
      top: auto;
      margin-top: 0;
      margin-bottom: var(--v2-space-1);
      min-width: 180px;
      right: 0;
      left: auto;
    }
  }

  &__cart-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-1);
    flex: 1;
    font-size: 0.8125rem;
    font-family: var(--v2-font-sans);
    font-weight: 600;
    background: var(--v2-primary);
    border: none;
    color: var(--v2-primary-foreground);
    cursor: pointer;
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom-left-radius: var(--v2-radius-lg);
    transition: opacity 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--in-cart {
      background: var(--v2-secondary);
      color: var(--v2-secondary-foreground);
    }
  }
}
</style>
